import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Form,
  Dropdown,
  Modal,
  Accordion,
  Card,
  Image,
  Container,
} from "react-bootstrap";
import { FaSearch, FaCopy } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import apiKeyIcon from "../assets/images/icons/apiKey.png";
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import { apiKeyService } from '../services/apiKeyService';
import { accountService } from '../services/accountService';
import { userService } from '../services/userService';

const APIKeyDashboard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const recordsPerPage = 10;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const roles = decodedToken.roles || [];
        setIsSuperAdmin(roles.includes("SUPERADMIN"));
        setIsAdmin(roles.includes("ADMIN"));
      } catch (err) {
        console.error("Token decode error:", err);
      }
    }
  }, []);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        if (isSuperAdmin) {
          const res = await accountService.getAccountConfigs();
          setCompanies(res);
          if (res.length > 0) {
            setSelectedCompany(res[0].id);
            await fetchApiKeys(res[0].id);
          }
        } else if (isAdmin) {
          const token = localStorage.getItem("token");
          const decoded = jwtDecode(token);
          const username = decoded.sub;

          const userRes = await userService.getUserByUsername(username);

          const fetchedAccountId = userRes.accountId;

          setCompanyId(fetchedAccountId);
          setSelectedCompany(fetchedAccountId);
          setCompanies([{ id: fetchedAccountId }]);

          await fetchApiKeys(fetchedAccountId);
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
        setErrorMessage("Failed to fetch company information: " + err.message);
      }
    };

    fetchCompanyDetails();
  }, [isSuperAdmin, isAdmin]);

  const fetchApiKeys = async (targetCompanyId, page = 1) => {
    setIsLoadingKeys(true);
    try {
      const response = await apiKeyService.getApiKeysByAccount(targetCompanyId, {
        page: page - 1,
        size: recordsPerPage,
        sortBy: 'validUntil',
        sortDirection: 'ASC'
      });
      setApiKeys(response.content || []);
      setTotalPages(response.totalPages || 0);
      setCurrentPage((response.page || 0) + 1);
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setApiKeys([]);
      setTotalPages(0);
    } finally {
      setIsLoadingKeys(false);
    }
  };

  const handleCreateApiKey = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const targetCompanyId = isSuperAdmin ? selectedCompany : companyId;

      const response = await apiKeyService.generateApiKey(targetCompanyId, "31536000");

      setGeneratedKey(response);
      setShowModal(true);
      await fetchApiKeys(targetCompanyId, currentPage);
    } catch (err) {
      setErrorMessage("Failed to generate API key");
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await apiKeyService.revokeApiKey(id);
      const targetCompanyId = isSuperAdmin ? selectedCompany : companyId;
      await fetchApiKeys(targetCompanyId, currentPage);
      setShowDeleteModal(false);
      
      toast.success("API key revoked successfully", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      setErrorMessage("Failed to revoke API key");
      setShowErrorModal(true);
      
      toast.error("Failed to revoke API key", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleCopyClick = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  // Using server-side pagination; apiKeys already represents current page records

  const handlePageChange = (pageNumber) => {
    const targetCompanyId = isSuperAdmin ? selectedCompany : companyId;
    fetchApiKeys(targetCompanyId, pageNumber);
  };

  return (
    <Container
      fluid
      className="ps-0 pb-4 pe-0 overflow-hidden user-management-container bg-white"
    >
      <Card className="card-nostyle">
        <Card.Body className="ps-0 pe-0 p-0">
          <div className="custom-domain-containers">
            <div
              className="d-flex justify-content-between pt-2 pb-2 ps-4 pe-4 align-items-center"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >

                <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 mb-0 fs-20 poppins fw-600">
                   <Image src={apiKeyIcon} className="me-1" width="20" /> API-Key
                </h4>
                <Button
                  variant="success"
                  className="br-radius-8"
                  onClick={() => {
                    setShowModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} className="fs-16 me-1" />{" "}
                  Create API Key
                </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
      <>
        {/* <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>API Key History</h5>
                    <Button
                        variant="success"
                        className="rounded-pill"
                        onClick={() => {
                            if (isSuperAdmin) {
                                setShowModal(true);
                            } else if (isAdmin) {
                                handleCreateApiKey();
                            }
                        }}
                    >
                        Create API Key
                    </Button>
                </div> */}

        {/* <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>API Key History</h5>
                    <Button
                        variant="success"
                        className="rounded-pill"
                        onClick={() => {
                            if (isSuperAdmin) {
                                setShowModal(true);
                            } else if (isAdmin) {
                                handleCreateApiKey();
                            }
                        }}
                    >
                        Create API Key
                    </Button>
                </div> */}

        {isSuperAdmin && (
          <Accordion
            onSelect={(eventKey) => {
              setApiKeys([]);
              if (eventKey) {
                setSelectedCompany(eventKey);
                fetchApiKeys(eventKey, 1);
              }
            }}
          >
            {companies.map((company) => (
              <Accordion.Item key={company.id} eventKey={company.id}>
                <Accordion.Header>
                  <div className="d-flex align-items-center">
                    {company.accountName}
                    {company.isActive ? (
                      <span className="badge bg-success ms-2">Active</span>
                    ) : (
                      <span className="badge bg-secondary ms-2">Inactive</span>
                    )}
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>API Key</th>
                        <th>Created On</th>
                        <th>Valid Until</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingKeys ? (
                        <tr>
                          <td colSpan="3" className="text-center">
                            <div className="d-flex justify-content-center py-3">
                              <div
                                className="spinner-border text-primary"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : apiKeys.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="text-center">
                            No API keys Generated
                          </td>
                        </tr>
                      ) : (
                        apiKeys.map((key) => (
                          <tr key={key.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <span className="me-2">{key.key}</span>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleCopyClick(key.key)}
                                >
                                  <FaCopy />
                                </Button>
                              </div>
                            </td>
                            <td>{new Date(key.createAt).toLocaleString()}</td>
                            <td>{new Date(key.validUntil).toLocaleString()}</td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  setKeyToDelete(key);
                                  setShowDeleteModal(true);
                                }}
                              >
                                Revoke
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        )}

        {isAdmin &&
          companies.map((company) => (
            <div key={company.id} className="mb-3 pe-4 ps-4 mt-2">
              <div className="d-flex align-items-center fw-bold mb-2">
                {company.accountName}
              </div>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>API Key</th>
                    <th>Created On</th>
                    <th>Valid Until</th>
                    <th className="text-end">Revoke token</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingKeys ? (
                    <tr>
                      <td colSpan="3" className="text-center">
                        <div className="d-flex justify-content-center py-3">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : apiKeys.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center">
                        No API keys Generated
                      </td>
                    </tr>
                  ) : (
                    apiKeys.map((key) => (
                      <tr key={key.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="me-2">{key.key}</span>
                          </div>
                        </td>
                        <td>{new Date(key.createAt).toLocaleString()}</td>
                        <td>{new Date(key.validUntil).toLocaleString()}</td>
                        <td className="text-end">
                          <Button
                            variant="outline-danger rounded-pill p-1 ps-3 pe-3"
                            size="sm"
                            onClick={() => {
                              setKeyToDelete(key);
                              setShowDeleteModal(true);
                            }}
                          >
                            Revoke
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              {apiKeys.length > 0 && (
                <div className="d-flex justify-content-center mt-4">
                  <nav>
                    <ul className="pagination mb-2">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                        >
                          &laquo;
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          &lt;
                        </button>
                      </li>
                      {(() => {
                        const startPage = Math.max(currentPage - 1, 1);
                        const endPage = Math.min(startPage + 2, totalPages);
                        
                        const adjustedStartPage = endPage === totalPages ? Math.max(endPage - 2, 1) : startPage;

                        return [...Array(endPage - adjustedStartPage + 1)].map((_, idx) => (
                          <li
                            key={adjustedStartPage + idx}
                            className={`page-item ${currentPage === adjustedStartPage + idx ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(adjustedStartPage + idx)}
                            >
                              {adjustedStartPage + idx}
                            </button>
                          </li>
                        ));
                      })()}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          &gt;
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          ))}
      </>

      <Modal aria-labelledby="contained-modal-title-vcenter"
        centered show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton className="border-0 bg-white">
          <Modal.Title className="w-100 text-center text-101828 mb-1 fw-600 ls-minus-0 fs-14">
            Create API Key
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-0">
          {!generatedKey && (
            <>
              {isSuperAdmin && (
                <Form.Group className="mb-3">
                  <Form.Label>Select Company</Form.Label>
                  <Form.Select
                    value={selectedCompany || ""}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="">Choose...</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.accountName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              <div className="text-center">
                <p className="fw-400 fs-12 mb-3">
                  {isSuperAdmin 
                    ? "Select a company and click Generate to create a new API key." 
                    : "Click Generate to create a new API key for your account."
                  }
                </p>
                <div className="domain-info mb-2">
                  <p className="text-616161 mb-0 fs-11 p-0 fw-400 ls-spacing-0">
                    <span className="fw-600 text-dark">Note: </span>
                    The API key will be displayed only once after creation. Make sure to copy and store it securely.
                  </p>
                </div>
              </div>
            </>
          )}

          {generatedKey && (
            <>
              <div className="">
                <strong className="fs-12 fw-500 col-sm-2">API Key:</strong>
                <div className="d-flex align-items-center mt-1">
                  <div className="bg-white border p-2 rounded flex-grow-1 fs-14">
                    {generatedKey.key}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleCopyClick(generatedKey.key)}
                    className="ms-2"
                  >
                    <FaCopy /> {copySuccess ? "Copied!" : "Copy"}
                  </Button>
                </div>
                <div className="domain-info mt-3 mb-2">
                  <p className="text-616161 mb-0 fs-11 p-0 fw-400 ls-spacing-0">
                    <span className="fw-600 text-dark">Note: </span>
                    Here is your new API token. <strong className="fw-500 text-black">This is the only time the token will ever be displayed, so be sure not to lose it!</strong> You may revoke the token at any time from your API settings.
                  </p>
                </div>
              </div>
              <div>
                <strong className="fw-600 fs-12">Valid Until:</strong>{" "}
                <span className="fs-12">{new Date(generatedKey.validUntil).toLocaleString()}</span>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
         <div className="d-flex justify-content-center gap-3 w-75">
          {!generatedKey && (
            <Button
              variant="success"
              className="br-radius-8"

              onClick={handleCreateApiKey}
              disabled={loading || (isSuperAdmin && !selectedCompany)}
            >
              {loading ? "Generating..." : "Generate"}
            </Button>
          )}
          <Button
              variant="light bg-white"
              className="br-radius-8"
            onClick={() => {
              setShowModal(false);
              setGeneratedKey(null);
              setSelectedCompany(null);
            }}
          >
            {generatedKey ? "Close" : "Cancel"}
          </Button>
        </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0 bg-white pb-2">
        </Modal.Header>
        <Modal.Body className="text-center p-0">
        <h5 class="text-101828 mb-1 fw-600 ls-minus-0 fs-14">Confirm Delete</h5>
            <p className="fw-400 fs-12 mb-1">Are you sure you want to revoke this API key?</p>  
            </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDeleteKey(keyToDelete.id)}
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer />
    </Container>
  );
};
export default APIKeyDashboard;
